import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const LayerSchema = z
    .object({
        url: z.string().trim(),
    })
    .brand<"Layer">();

export type LayerInputProps = z.input<typeof LayerSchema>;
type LayerProps = z.output<typeof LayerSchema>;

export class Layer {
    private constructor(private readonly props: Readonly<LayerProps>) {}

    get url(): string {
        return this.props.url;
    }

    static validate(
        props: Readonly<MaybeValidProps<LayerInputProps>>,
    ): Result<LayerProps, z.ZodError<LayerProps>> {
        const result = LayerSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<LayerInputProps>>,
    ): Result<LayerProps, PartialValid<typeof Layer>> {
        const result = partialSafeParse(LayerSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<LayerInputProps>,
    ): Result<Layer, z.ZodError<LayerProps>> {
        return Layer.validate(props).map(
            (validatedProps) => new Layer(validatedProps),
        );
    }

    static createWithRecovery(
        props: LayerInputProps,
    ): Result<Layer, ModelError<typeof Layer>> {
        return Layer.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.url)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<LayerProps> = {
                    url: "",
                };

                const recoveryProps: Unbrand<LayerProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Layer>(
                    new Layer(recoveryProps as LayerProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Layer(validatedProps));
    }

    toCreateProps(): LayerInputProps {
        return {
            url: this.props.url,
        };
    }
}

export const LayerCreateSchema = z.object({
    layer: z.string().trim().min(1),
    weeksAgo: z.number().min(1).default(1),
    source: z.string().trim().min(1),
});
export type LayerCreate = z.output<typeof LayerCreateSchema>;
